import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, Modal, Tooltip } from '@playerbase/ui';
import type {
  Playtest,
  PlaytestEventInput,
  PlaytestParticipationRule,
  PlaytestQuestionInput,
} from '@playerbase/types';
import { DEFAULT_PLAYTEST_BUTTON_LABEL } from '@playerbase/types';

import { useShell } from '../../../../shell/use-shell.js';
import { usePlaytestsRealtime } from '../../../../games/use-playtests-realtime.js';
import {
  activatePlaytest,
  fetchPlaytest,
  fetchPlaytestTree,
  replacePlaytestEvents,
  setPlaytestParticipationRule,
  updatePlaytest,
  type PlaytestRpcError,
} from '../../../../games/playtests-data.js';
import {
  builderStateFromServer,
  usePlaytestBuilderState,
  type BuilderEvent,
  type BuilderMetadata,
  type BuilderQuestion,
} from '../../../../games/use-playtest-builder-state.js';
import { MetadataCard } from './metadata-card.js';
import { EventsCard } from './events-card.js';
import { ParticipationRuleCard } from './participation-rule-card.js';
import { PreviewModal } from './preview-modal.js';
import { DisableConfirmModal } from './disable-confirm-modal.js';

interface SyncSnapshot {
  metadata: BuilderMetadata;
  events: BuilderEvent[];
  participationRule: PlaytestParticipationRule;
}

const ERROR_COPY: Partial<Record<PlaytestRpcError, string>> = {
  not_a_member: "You don't have permission to edit this playtest.",
  playtest_active_cannot_edit: 'Archive the playtest before editing it.',
  name_required: 'A playtest name is required.',
  name_too_long: 'Playtest name must be 80 characters or fewer.',
  event_label_required: 'Every event needs a label.',
  event_name_required: 'Every event needs an SDK name.',
  event_name_too_long: 'SDK event names are limited to 80 characters.',
  event_name_invalid:
    'SDK event names must start with a letter or underscore and contain only letters, digits and underscores.',
  event_headline_too_long: 'Event headlines are capped at 45 characters (Discord modal title).',
  event_button_label_too_long: 'Button labels are capped at 80 characters (Discord button label).',
  event_too_many_questions: 'Events can have at most 5 questions (Discord modal cap).',
  question_prompt_required: 'Every question needs a prompt.',
  question_prompt_too_long: 'Question prompts are capped at 45 characters (Discord modal label).',
  question_help_text_too_long: 'Helper text is capped at 100 characters.',
  rating_display_must_be_object: 'Rating display config is invalid.',
  rating_display_mode_invalid: 'Rating display mode must be numbers or emoji.',
  rating_display_emoji_invalid: 'Pick an emoji (1-8 chars) for emoji-mode ratings.',
  binary_label_must_be_string: 'Binary labels must be text or emoji.',
  binary_label_too_long: 'Binary labels are limited to 32 characters.',
  select_options_required: 'Select questions need at least one option.',
  select_needs_at_least_one_option: 'Select questions need at least one option.',
  select_too_many_options: 'Select questions are capped at 25 options.',
  select_max_values_invalid: 'Max selections must be between 1 and the option count.',
  participation_rule_channels_required: 'Pick at least one channel for the channel-members rule.',
  participation_rule_roles_required: 'Pick at least one role for the role-members rule.',
  invalid_question_kind: 'One of the question kinds is no longer supported.',
  playtest_needs_events: 'Add at least one event before activating.',
  playtest_event_needs_questions: 'Every event needs at least one question before you can activate.',
  playtest_archived: 'This playtest is archived. Unarchive it from the list to activate.',
};

// Compare a server-side BuilderEvent / question tree against in-memory
// state without caring about serverId reconciliation. Mirrors the
// fieldShape helper from the form builder.
function eventsEqual(a: BuilderEvent[], b: BuilderEvent[]): boolean {
  return JSON.stringify(a.map(stripServerIds)) === JSON.stringify(b.map(stripServerIds));
}

function stripServerIds(event: BuilderEvent): Omit<BuilderEvent, 'serverId'> {
  return {
    clientId: event.clientId,
    name: event.name,
    label: event.label,
    description: event.description,
    headline: event.headline,
    buttonLabel: event.buttonLabel,
    questions: event.questions.map((q) => ({ ...q, serverId: null })),
  };
}

// Builder -> RPC wire mapping. Mirrors the feedback-form save path:
// we ship the whole event tree to replace_playtest_events; the RPC
// validates per-kind config server-side.
function questionToWire(question: BuilderQuestion): PlaytestQuestionInput {
  const base = {
    prompt: question.title === '' ? 'Untitled question' : question.title,
    helpText: question.description,
    required: question.required,
  } as const;
  if (
    question.kind === 'short_text'
    || question.kind === 'long_text'
    || question.kind === 'severity'
    || question.kind === 'nps'
  ) {
    return { kind: question.kind, ...base, config: {} };
  }
  if (question.kind === 'rating') {
    const config = question.config as { display?: { mode: 'emoji'; emoji: string } };
    return {
      kind: 'rating',
      ...base,
      config: config.display?.mode === 'emoji' ? { display: config.display } : {},
    };
  }
  if (question.kind === 'select') {
    const config = question.config as { options: string[]; maxValues?: number };
    const options = config.options.map((o) => o.trim()).filter((o) => o.length > 0);
    const finalOptions = options.length === 0 ? ['Option 1'] : options;
    const clamped =
      config.maxValues !== undefined && config.maxValues > 1
        ? Math.min(config.maxValues, finalOptions.length)
        : undefined;
    return {
      kind: 'select',
      ...base,
      config:
        clamped !== undefined
          ? { options: finalOptions, maxValues: clamped }
          : { options: finalOptions },
    };
  }
  // binary
  const config = question.config as { trueLabel?: string; falseLabel?: string };
  const out: { trueLabel?: string; falseLabel?: string } = {};
  if (config.trueLabel !== undefined && config.trueLabel.length > 0) {
    out.trueLabel = config.trueLabel;
  }
  if (config.falseLabel !== undefined && config.falseLabel.length > 0) {
    out.falseLabel = config.falseLabel;
  }
  return { kind: 'binary', ...base, config: out };
}

function eventsToWire(events: BuilderEvent[]): PlaytestEventInput[] {
  return events.map((event) => ({
    name: event.name === '' ? 'untitled_event' : event.name,
    label: event.label,
    description: event.description,
    headline: event.headline,
    buttonLabel:
      event.buttonLabel === '' ? DEFAULT_PLAYTEST_BUTTON_LABEL : event.buttonLabel,
    questions: event.questions.map(questionToWire),
  }));
}

export function PlaytestBuilderPage(): JSX.Element {
  const params = useParams<{ playtestId: string }>();
  const { playtestId } = params;
  const { activeGame } = useShell();
  const { playtestsVersion } = usePlaytestsRealtime();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [activateConfirmOpen, setActivateConfirmOpen] = useState(false);
  const [activating, setActivating] = useState(false);
  const [activateError, setActivateError] = useState<string | null>(null);
  const [disableConfirmOpen, setDisableConfirmOpen] = useState(false);
  const [playtest, setPlaytest] = useState<Playtest | null>(null);

  const builder = usePlaytestBuilderState({
    metadata: { name: '', internalDescription: '' },
    events: [],
    lastError: null,
  });

  const syncedRef = useRef<SyncSnapshot | null>(null);
  const stateRef = useRef(builder.state);
  stateRef.current = builder.state;
  const builderRef = useRef(builder);
  builderRef.current = builder;
  // Participation rule lives in proper React state (not a ref) so the card
  // re-renders on selection. Mirror it to a ref for the load path to read
  // without depending on closure freshness.
  const [participationRule, setParticipationRule] = useState<PlaytestParticipationRule>({
    kind: 'open',
  });
  const ruleRef = useRef(participationRule);
  ruleRef.current = participationRule;

  const reload = useCallback(async (): Promise<void> => {
    if (playtestId === undefined) return;
    const [loadedPlaytest, tree] = await Promise.all([
      fetchPlaytest(playtestId),
      fetchPlaytestTree(playtestId),
    ]);
    if (loadedPlaytest === null) {
      setLoadError('Playtest not found.');
      setLoading(false);
      return;
    }
    setPlaytest(loadedPlaytest);
    setParticipationRule(loadedPlaytest.participationRule);
    const seed = builderStateFromServer(
      {
        name: loadedPlaytest.name,
        internalDescription: loadedPlaytest.internalDescription,
      },
      tree,
    );
    const snapshot: SyncSnapshot = {
      metadata: seed.metadata,
      events: seed.events,
      participationRule: loadedPlaytest.participationRule,
    };

    if (syncedRef.current === null) {
      builderRef.current.reset(seed);
      syncedRef.current = snapshot;
      setLoading(false);
      return;
    }

    const localDirty =
      JSON.stringify(stateRef.current.metadata) !==
        JSON.stringify(syncedRef.current.metadata) ||
      !eventsEqual(stateRef.current.events, syncedRef.current.events) ||
      JSON.stringify(ruleRef.current) !==
        JSON.stringify(syncedRef.current.participationRule);
    if (localDirty) return;

    const echoesSynced =
      JSON.stringify(snapshot.metadata) ===
        JSON.stringify(syncedRef.current.metadata) &&
      eventsEqual(snapshot.events, syncedRef.current.events) &&
      JSON.stringify(snapshot.participationRule) ===
        JSON.stringify(syncedRef.current.participationRule);
    if (echoesSynced) return;

    builderRef.current.reset(seed);
    syncedRef.current = snapshot;
  }, [playtestId]);

  useEffect(() => {
    void reload();
  }, [reload, playtestsVersion]);

  const dirty = (() => {
    const synced = syncedRef.current;
    if (synced === null) return false;
    return (
      JSON.stringify(synced.metadata) !== JSON.stringify(builder.state.metadata) ||
      !eventsEqual(synced.events, builder.state.events) ||
      JSON.stringify(synced.participationRule) !== JSON.stringify(participationRule)
    );
  })();

  useEffect(() => {
    if (!dirty) return undefined;
    function onBeforeUnload(e: BeforeUnloadEvent): void {
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);


  async function handleSave(): Promise<void> {
    if (playtestId === undefined) return;
    setSaving(true);
    setSaveError(null);

    const snapshot: SyncSnapshot = {
      metadata: builder.state.metadata,
      events: builder.state.events,
      participationRule,
    };
    const synced = syncedRef.current;

    let firstError: PlaytestRpcError | null = null;

    const metadataChanged =
      synced === null ||
      JSON.stringify(synced.metadata) !== JSON.stringify(snapshot.metadata);
    if (metadataChanged) {
      const result = await updatePlaytest(playtestId, {
        name:
          snapshot.metadata.name === '' ? 'Untitled playtest' : snapshot.metadata.name,
        internalDescription:
          snapshot.metadata.internalDescription === ''
            ? null
            : snapshot.metadata.internalDescription,
      });
      if (!result.ok) firstError = result.error;
      else if (result.data !== null) setPlaytest(result.data);
    }

    if (firstError === null) {
      const eventsChanged =
        synced === null || !eventsEqual(synced.events, snapshot.events);
      if (eventsChanged) {
        const result = await replacePlaytestEvents(
          playtestId,
          eventsToWire(snapshot.events),
        );
        if (!result.ok) firstError = result.error;
      }
    }

    if (firstError === null) {
      const ruleChanged =
        synced === null ||
        JSON.stringify(synced.participationRule) !==
          JSON.stringify(snapshot.participationRule);
      if (ruleChanged) {
        const result = await setPlaytestParticipationRule(
          playtestId,
          snapshot.participationRule,
        );
        if (!result.ok) firstError = result.error;
        else if (result.data !== null) setPlaytest(result.data);
      }
    }

    if (firstError !== null) {
      setSaving(false);
      setSaveError(
        ERROR_COPY[firstError] ?? 'Could not save the playtest. Please try again.',
      );
      return;
    }
    syncedRef.current = snapshot;
    setSaving(false);
  }

  async function handleActivate(): Promise<void> {
    if (playtestId === undefined) return;
    setActivating(true);
    setActivateError(null);
    const result = await activatePlaytest(playtestId);
    setActivating(false);
    if (!result.ok) {
      setActivateError(
        ERROR_COPY[result.error ?? 'unknown']
          ?? 'Could not activate the playtest. Please try again.',
      );
      return;
    }
    if (result.data !== null) {
      setPlaytest(result.data);
    }
    setActivateConfirmOpen(false);
  }

  if (loading) {
    return <div className="px-2 py-6 text-sm text-text-muted">Loading playtest...</div>;
  }
  if (loadError !== null) {
    return (
      <div className="px-2 py-6">
        <Alert tone="danger">{loadError}</Alert>
      </div>
    );
  }
  if (playtestId === undefined || activeGame === null) {
    return <div className="px-2 py-6 text-sm text-text-muted">Loading...</div>;
  }

  function handleBack(): void {
    if (dirty) {
      setDiscardConfirmOpen(true);
      return;
    }
    navigate('..');
  }

  const headingTitle =
    builder.state.metadata.name.trim() === ''
      ? 'Untitled playtest'
      : builder.state.metadata.name;
  const status = playtest?.status ?? 'draft';
  const isActive = status === 'active';

  return (
    <div className="flex min-h-full flex-1 flex-col">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-5 px-6 py-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-text-muted">
              Playtest
            </span>
            <h1 className="font-display text-2xl font-bold text-text">{headingTitle}</h1>
            <div className="flex items-center gap-2 text-xs text-text-muted">
              <span className="rounded-sm border border-border bg-surface-2 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider">
                {status === 'active' ? 'Active' : status === 'disabled' ? 'Disabled' : 'Draft'}
              </span>
              <span>
                {isActive ? 'Active - disable to edit' : dirty ? 'Unsaved changes' : 'All changes saved'}
              </span>
            </div>
          </div>
        </div>

        {saveError !== null ? <Alert tone="danger">{saveError}</Alert> : null}
        {isActive ? (
          <Alert tone="info">This playtest is active. Disable it before making changes.</Alert>
        ) : null}

        <div
          className={isActive ? 'pointer-events-none select-none opacity-60' : undefined}
          aria-disabled={isActive}
        >
          <div className="flex flex-col gap-5">
            <MetadataCard metadata={builder.state.metadata} builder={builder} />
            <EventsCard builder={builder} />
            <ParticipationRuleCard
              rule={participationRule}
              onChange={setParticipationRule}
            />
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 z-10 mt-auto border-t border-border bg-surface">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-between gap-3 px-6 py-3">
          <span className="text-xs text-text-muted">
            {isActive ? 'Active - read-only' : dirty ? 'Unsaved changes' : 'Up to date'}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" type="button" onClick={handleBack}>
              ← Exit
            </Button>
            <Button variant="ghost" type="button" onClick={() => setPreviewOpen(true)}>
              Preview
            </Button>
            {isActive ? null : (
              <Button
                variant="primary"
                type="button"
                onClick={() => void handleSave()}
                disabled={saving || !dirty}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            )}
            {isActive ? (
              <Button
                variant="danger"
                type="button"
                onClick={() => setDisableConfirmOpen(true)}
              >
                Disable
              </Button>
            ) : dirty ? (
              <Tooltip content="Save your changes first">
                <Button variant="primary" type="button" disabled aria-disabled>
                  Activate
                </Button>
              </Tooltip>
            ) : (
              <Button
                variant="primary"
                type="button"
                onClick={() => {
                  setActivateError(null);
                  setActivateConfirmOpen(true);
                }}
              >
                Activate
              </Button>
            )}
          </div>
        </div>
      </div>

      <PreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        events={builder.state.events}
      />

      <Modal
        open={discardConfirmOpen}
        onClose={() => setDiscardConfirmOpen(false)}
        labelledById="discard-playtest-changes-title"
        size="sm"
      >
        <Card>
          <Card.Header>
            <Card.Title id="discard-playtest-changes-title">
              Leave without saving?
            </Card.Title>
            <Card.Description>
              You have unsaved changes to this playtest. They will be lost if
              you leave now.
            </Card.Description>
          </Card.Header>
          <Card.Footer>
            <Button variant="ghost" onClick={() => setDiscardConfirmOpen(false)}>
              Keep editing
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                setDiscardConfirmOpen(false);
                navigate('..');
              }}
            >
              Discard changes
            </Button>
          </Card.Footer>
        </Card>
      </Modal>

      <Modal
        open={activateConfirmOpen}
        onClose={() => {
          if (!activating) {
            setActivateError(null);
            setActivateConfirmOpen(false);
          }
        }}
        labelledById="activate-playtest-title"
        busy={activating}
        size="sm"
      >
        <Card>
          <Card.Header>
            <Card.Title id="activate-playtest-title">Activate playtest</Card.Title>
            <Card.Description>
              While active, paired testers matching the participation rule
              will receive a Discord DM whenever your game fires one of this
              playtest&rsquo;s events. Disable it later to make changes.
            </Card.Description>
          </Card.Header>
          {activateError !== null ? (
            <Card.Content>
              <Alert tone="danger">{activateError}</Alert>
            </Card.Content>
          ) : null}
          <Card.Footer>
            <Button
              variant="ghost"
              onClick={() => {
                setActivateError(null);
                setActivateConfirmOpen(false);
              }}
              disabled={activating}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void handleActivate()}
              disabled={activating}
            >
              {activating ? 'Activating…' : 'Activate'}
            </Button>
          </Card.Footer>
        </Card>
      </Modal>

      {playtest !== null ? (
        <DisableConfirmModal
          open={disableConfirmOpen}
          playtest={playtest}
          onClose={() => setDisableConfirmOpen(false)}
          onDisabled={(updated) => {
            setPlaytest(updated);
            setDisableConfirmOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}
